// Category / owner (bucket) controls and activity history for one Delivery Hub query
import { useState } from 'react';
import { History, Loader2 } from 'lucide-react';
import {
  ClientQuery,
  QueryActivity,
  QueryCategory,
  QueryOwner,
  getQueryActivity,
  updateQuery,
} from '@/actions/delivery-hub';
import { showToast } from '@/components/ui/Toast';

const STATUS_LABELS: Record<string, string> = { open: 'Open', in_progress: 'In progress', resolved: 'Resolved' };

const describeActivity = (a: QueryActivity) => {
  const who = a.actor_name ? ` by ${a.actor_name}` : '';
  switch (a.action) {
    case 'created':
      return `Raised${a.to_value ? ` under ${a.to_value}` : ''}${who}`;
    case 'assigned':
      if (a.metadata?.field === 'category') return `Category changed ${a.from_value || '—'} → ${a.to_value}${who}`;
      return a.from_value ? `Reassigned ${a.from_value} → ${a.to_value}${who}` : `Assigned to ${a.to_value}${who}`;
    case 'status_changed':
      return `Status ${STATUS_LABELS[a.from_value || ''] || a.from_value} → ${STATUS_LABELS[a.to_value || ''] || a.to_value}${who}`;
    case 'reopened':
      return `Reopened${who}`;
    case 'whatsapp_sent':
      return `WhatsApp sent to ${a.to_value}`;
    case 'whatsapp_failed':
      if (a.metadata?.test_mode) return `WhatsApp to ${a.to_value} skipped (test mode, ${a.metadata.template})`;
      return `WhatsApp to ${a.to_value} failed${a.metadata?.error ? `: ${a.metadata.error}` : ''}`;
    default:
      return a.action;
  }
};

const formatWhen = (d: string) =>
  new Date(d).toLocaleString('en-IN', { day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' });

export default function QueryRoutingControls({
  query,
  categories,
  owners,
  onUpdated,
}: {
  query: ClientQuery;
  categories: QueryCategory[];
  owners: QueryOwner[];
  onUpdated: (query: ClientQuery) => void;
}) {
  const [saving, setSaving] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [activity, setActivity] = useState<QueryActivity[] | null>(null);

  const loadActivity = async () => setActivity(await getQueryActivity(query.id));

  const save = async (data: { category_id?: number; assigned_owner_id?: number }) => {
    setSaving(true);
    const result = await updateQuery(query.id, data);
    setSaving(false);
    if (result.success && result.query) {
      onUpdated(result.query);
      showToast(data.assigned_owner_id ? `Moved to ${result.query.assigned_owner?.name}'s bucket` : 'Category updated', 'success');
      if (showHistory) loadActivity();
    } else {
      showToast(result.message || 'Failed to update query', 'error');
    }
  };

  const toggleHistory = () => {
    const next = !showHistory;
    setShowHistory(next);
    if (next) loadActivity();
  };

  const selectClass =
    'px-2 py-1 text-xs border border-gray-300 rounded-md bg-white focus:ring-1 focus:ring-brand focus:border-brand disabled:opacity-50';

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
        <label className="flex items-center gap-1.5">
          Category
          <select
            className={selectClass}
            value={query.category?.id ?? ''}
            disabled={saving}
            onChange={(e) => e.target.value && save({ category_id: Number(e.target.value) })}
          >
            {!query.category && <option value="">Uncategorized</option>}
            {categories.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-1.5">
          Owner
          <select
            className={selectClass}
            value={query.assigned_owner?.id ?? ''}
            disabled={saving}
            onChange={(e) => e.target.value && save({ assigned_owner_id: Number(e.target.value) })}
          >
            {!query.assigned_owner && <option value="">Unassigned</option>}
            {owners.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </label>
        {saving && <Loader2 size={14} className="animate-spin text-gray-400" />}
        <button
          type="button"
          onClick={toggleHistory}
          className={`flex items-center gap-1 ${showHistory ? 'text-brand' : 'text-gray-500 hover:text-brand'}`}
        >
          <History size={13} />
          History
        </button>
      </div>

      {showHistory && (
        <div className="mt-2 rounded-md border border-gray-100 bg-gray-50 px-3 py-2">
          {activity === null ? (
            <Loader2 size={14} className="animate-spin text-gray-400" />
          ) : activity.length === 0 ? (
            <p className="text-xs text-gray-500">No history recorded yet.</p>
          ) : (
            <ul className="space-y-1">
              {activity.map((a) => (
                <li
                  key={a.id}
                  className={`text-xs flex gap-2 ${a.action === 'whatsapp_failed' && !a.metadata?.test_mode ? 'text-red-600' : 'text-gray-600'}`}
                >
                  <span className="text-gray-400 whitespace-nowrap">{formatWhen(a.created_at)}</span>
                  <span>{describeActivity(a)}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}
